import { computed, nextTick, onMounted, reactive, ref, watch, shallowRef } from 'vue';

export interface Props {
  data: any[];
  itemHeight: number;
  itemWidth?: number;
  gap?: number;
  overscanRowCount?: number; // 앞뒤 여분으로 더 불러올 행 수
  keyField?: string; // 아이템 고유 식별자 필드 (고유 키 있을 때 사용)
}

export const setup = (props: Props) => {
  const listRef = ref();
  const visibleList = shallowRef<any[]>([]);

  const allList = computed(() => props.data);

  const vScrollInfo = reactive<{
    lastScrollTop: number;
    top: number;
    bottom: number;
  }>({
    lastScrollTop: 0,
    top: 0,
    bottom: 0,
  });

  const getVisibleRowCount = () => {
    const listEl: HTMLElement = listRef.value;
    const listHeight: number = listEl.clientHeight;
    const listWidth: number = listEl.clientWidth;

    const totalCount = allList.value.length;
    const gap = props.gap ?? 0;

    // gap을 반영한 컬럼 수 계산
    const itemsPerRow = props.itemWidth
      ? Math.max(Math.floor((listWidth + gap) / (props.itemWidth + gap)), 1)
      : 1;

    const totalRowCount = Math.ceil(totalCount / itemsPerRow);

    // gap을 포함한 행 높이
    const effectiveRowHeight = props.itemHeight + gap;

    const visibleRowCount =
      listHeight > effectiveRowHeight
        ? Math.ceil(listHeight / effectiveRowHeight) + 1
        : totalRowCount;

    return {
      itemsPerRow,
      totalRowCount,
      visibleRowCount,
      effectiveRowHeight,
    };
  };

  const getStartEndRowIndex = (
    totalRowCount: number,
    visibleRowCount: number,
    effectiveRowHeight: number,
  ) => {
    const listEl: HTMLElement = listRef.value;
    const index = Math.max(Math.round(listEl.scrollTop / effectiveRowHeight), 0);

    const rawStartRowIndex = Math.min(index, Math.max(totalRowCount - visibleRowCount, 0));
    const startRowIndex = Math.max(rawStartRowIndex - (props.overscanRowCount ?? 0), 0);
    const endRowIndex = startRowIndex + visibleRowCount + (props.overscanRowCount ?? 0) + 1;

    return {
      startRowIndex,
      endRowIndex,
    };
  };

  const updateVirtualScroll = () => {
    const listEl: HTMLElement = listRef.value;
    if (!listEl) return;

    const gap = props.gap ?? 0;
    const { itemsPerRow, totalRowCount, visibleRowCount, effectiveRowHeight } =
      getVisibleRowCount();
    const { startRowIndex, endRowIndex } = getStartEndRowIndex(
      totalRowCount,
      visibleRowCount,
      effectiveRowHeight,
    );

    const clampedEndRowIndex = Math.min(endRowIndex, totalRowCount);
    const afterRows = totalRowCount - clampedEndRowIndex;

    visibleList.value = allList.value.slice(
      startRowIndex * itemsPerRow,
      clampedEndRowIndex * itemsPerRow,
    );

    vScrollInfo.top = startRowIndex > 0 ? startRowIndex * effectiveRowHeight - gap : 0;
    vScrollInfo.bottom = afterRows > 0 ? afterRows * effectiveRowHeight - gap : 0;
  };

  const onScroll = () => {
    const listEl: HTMLElement = listRef.value;
    if (listEl) {
      const { scrollTop } = listEl;
      const lastTop = vScrollInfo.lastScrollTop;

      if (scrollTop !== lastTop && listEl.clientHeight) {
        updateVirtualScroll();
      }

      vScrollInfo.lastScrollTop = scrollTop;
    }
  };

  const initVScrollInfo = () => {
    vScrollInfo.lastScrollTop = 0;
    vScrollInfo.top = 0;
    vScrollInfo.bottom = 0;

    const listEl: HTMLElement = listRef.value;
    if (listEl) {
      listEl.scrollTop = 0;
    }
  };

  watch(
    () => props.data,
    async () => {
      await nextTick();
      updateVirtualScroll();
    },
  );

  onMounted(async () => {
    await nextTick();
    updateVirtualScroll();
  });

  return {
    listRef,
    visibleList,
    vScrollInfo,
    onScroll,
    initVScrollInfo,
    updateVirtualScroll,
  };
};
